import styled, { keyframes } from 'styled-components';
import { AgGridThemedWrap } from '../../../components/Tables/StatGrid/StatGrid.styles';

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

/* Tiny inline 3-dot loader for use inside table cells while async content
   loads. Pure-CSS animation; no SVG, no JS. */
export const LoadingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.tiny};

  & > span {
    width: 4px;
    height: 4px;
    border-radius: ${({ theme }) => theme.borderRadius.ellipse};
    background: ${({ theme }) => theme.colours.ink.primary.weak};
    animation: ${blink} 1.2s ${({ theme }) => theme.animation.easing.moving}
      infinite both;
  }
  & > span:nth-child(2) {
    animation-delay: 0.15s;
  }
  & > span:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

export const TabsWrap = styled.div`
  margin-top: ${({ theme }) => theme.sizing.small};
  background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
  padding: ${({ theme }) =>
    `${theme.sizing.small} ${theme.sizing.medium} 0 ${theme.sizing.medium}`};

  /* override kitbag Tabs' internal horizontal padding */
  & > div > div {
    padding-inline: 0;
  }
`;

/* Bento layout — asymmetric blocks sized to content importance, anchored by
   the primary content (Scoring Plays) on the left. Matches the 2024 MDPI
   eye-tracking findings: core content on the left, supporting content on
   the right, partial-symmetry not strict-symmetry. */
export const SummaryLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  grid-template-rows: auto auto;
  grid-template-areas:
    'plays scoreboard'
    'plays epa';
  align-items: start;
  gap: ${({ theme }) => theme.sizing.small};
  margin-top: ${({ theme }) => theme.sizing.small};
`;

/* Card shell for the Play by Play tab. Matches the Summary tab treatment:
   no header/body divider, table flush with header, single border around the
   whole table. Accordion row styling is left to PlayByPlayTable. */
export const PlayByPlayCardWrap = styled.div`
  /* Card fills the viewport below the static page chrome (kitbag nav +
     page header + game score header + tabs). Header hugs content; body
     fills the rest and lets AG Grid handle its own scrolling. Offset is
     tuned so the bottom gap matches the page's horizontal edge gap. */
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: calc(100vh - 168px);

  article {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  article > header {
    border-bottom: none;
    flex: 0 0 auto;
  }

  article > div {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  article > div > div {
    padding-top: 0;
    flex: 1 1 auto;
    min-height: 0;
  }

  table {
    border: 1px solid ${({ theme }) => theme.colours.canvas.secondary.strong};
  }

  .ag-body {
    background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
  }

  /* Detail (expanded) sub-grid: force all backgrounds to canvas.tertiary.main
     (#121212) to match the card background and main grid rows.
     StatGrid.styles sets .ag-details-row to canvas.secondary.main and sets
     the --ag-* CSS variables on .ag-root-wrapper — those rules have the same
     or higher specificity than PlayByPlayCardWrap, so !important is needed on
     the CSS variables to guarantee the override wins regardless of injection
     order. */
  .ag-details-row {
    background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
  }

  .ag-details-row .ag-root-wrapper {
    --ag-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
    --ag-data-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
    --ag-odd-row-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
    --ag-header-background-color: ${({ theme }) =>
      theme.colours.canvas.primary.main} !important;
    --ag-overlay-background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
  }

  .ag-details-row .ag-body {
    background-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.main} !important;
  }
`;

/* Offense cell: coloured team dot + name. Inherits the surrounding cell
   typography (bodyMedium in data rows, headingSmall in headers) so the
   team name reads the same as other text in that context. */
export const OffenseCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.tiny};
`;

/* Play outcome cell: success/fail dot indicator + label. Mirrors the
   "Play outcome" column on the standalone Play by Play page. */
export const OutcomeCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.tiny};
`;

export const OutcomeDot = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: ${({ theme }) => theme.sizing.regular};
  height: ${({ theme }) => theme.sizing.regular};
  border-radius: ${({ theme }) => theme.borderRadius.ellipse};
  background-color: ${({ $colour }) => $colour};
`;

/* Wrapper for @hudl/uniform-web-grid <Grid>. Uniform's themes resolve
   colours / spacing / fonts via CSS custom properties prefixed --u-*,
   which aren't defined in this app. Define just the ones the Grid theme
   reads (see node_modules/@hudl/uniform-web-grid/dist/helper-*.mjs),
   mapping them to the equivalent kitbag tokens so the Grid inherits the
   rest of the app's look without per-prop overrides. */
export const UniformGridThemedWrap = styled.div`
  /* Spacing */
  --u-space-eighth: 1px;
  --u-space-quarter: ${({ theme }) => theme.sizing.xtiny};
  --u-space-half: ${({ theme }) => theme.sizing.small};
  --u-space-one: ${({ theme }) => theme.sizing.medium};
  --u-space-one-and-half: ${({ theme }) => theme.sizing.large};

  /* Colour: background / surface */
  --u-color-base-background: ${({ theme }) =>
    theme.colours.canvas.tertiary.main};
  --u-color-background-container: ${({ theme }) =>
    theme.colours.canvas.tertiary.main};
  --u-color-background-callout: ${({ theme }) =>
    theme.colours.canvas.tertiary.main};
  --u-color-background-popover: ${({ theme }) =>
    theme.colours.canvas.secondary.main};

  /* Colour: ink */
  --u-color-base-foreground: ${({ theme }) => theme.colours.ink.primary.main};
  --u-color-base-foreground-contrast: ${({ theme }) =>
    theme.colours.ink.primary.strong};
  --u-color-base-foreground-subtle: ${({ theme }) =>
    theme.colours.ink.primary.weak};

  /* Colour: lines */
  --u-color-line: ${({ theme }) => theme.colours.canvas.secondary.strong};
  --u-color-line-subtle: ${({ theme }) =>
    theme.colours.canvas.secondary.strong};

  /* Colour: interactions */
  --u-color-base-background-hover: ${({ theme }) =>
    theme.colours.canvas.secondary.main};
  --u-color-base-background-active: ${({ theme }) =>
    theme.colours.canvas.secondary.strong};
  --u-color-emphasis-foreground: ${({ theme }) => theme.colours.interface.main};
  --u-color-emphasis-background-hover: ${({ theme }) =>
    theme.colours.canvas.secondary.main};
  --u-color-alert-foreground: ${({ theme }) =>
    theme.colours.messaging.error.main};

  /* Typography */
  --u-font-body: inherit;
  --u-font-size-micro: ${({ theme }) => theme.typography.bodySmall.fontSize};
  --u-font-size-small: ${({ theme }) => theme.typography.bodySmall.fontSize};
  --u-font-size-default: ${({ theme }) => theme.typography.bodyMedium.fontSize};
  --u-font-size-large: ${({ theme }) => theme.typography.bodyRegular.fontSize};
  --u-font-weight-bold: ${({ theme }) =>
    theme.typography.headingSmall.fontWeight};
  --u-border-radius-small: 0;
`;

/* Card-header row: left-side mode switcher + right-side view switcher.
   Stretches the CardHeader contents into a justified flex row so each
   group sits flush to its edge. */
export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.sizing.small};
`;

/* Contextual selection action bar — sits next to the card title on the
   left side of the header while one or more plays are selected. */
export const SelectionActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.small};
`;

/* Left-side group inside the card header: title (or All/Scoring submode
   buttons) plus the contextual action bar when a selection exists. */
export const HeaderLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.medium};
  min-width: 0;
`;

/* Inline wrapper to put a kitbag Icon next to text inside a Button's
   children. Buttons don't expose an icon slot directly; flex + small gap
   keeps icon and label centred together. */
export const ViewSwitchButtonInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.tiny};
`;

/* Two-button mode switcher for the Play by Play card header — sits in place
   of the card title, lets the user toggle between All / Scoring drives. */
export const ModeSwitcher = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.small};
`;

/* Play by Play grid wrapper (drive table and plays grid): the shared grid
   wrapper, with three distinct surfaces: rows on canvas.tertiary.main,
   quarter dividers on canvas.secondary.main and the header row on
   canvas.primary.main. Other grids that use AgGridThemedWrap keep the
   default header colour. */
export const PlayByPlayGridWrap = styled(AgGridThemedWrap)`
  .ag-root-wrapper {
    --ag-header-background-color: ${({ theme }) =>
      theme.colours.canvas.primary.main};

    /* Checkboxes in the filter popups match the kitbag Checkbox used in the
       selection column: transparent box with a canvas.tertiary.ink outline
       when off; interface.main fill with a graphics.inverse.main tick when
       on or partly on. Uniform's own stylesheet sets these AG variables directly
       on .u-grid, so the grid theme settings cannot override them. */
    --ag-checkbox-border-radius: ${({ theme }) => theme.borderRadius.tertiary};
    --ag-checkbox-unchecked-background-color: transparent;
    --ag-checkbox-unchecked-border-color: ${({ theme }) =>
      theme.colours.canvas.tertiary.ink};
    --ag-checkbox-checked-background-color: ${({ theme }) =>
      theme.colours.interface.main};
    --ag-checkbox-checked-border-color: ${({ theme }) =>
      theme.colours.interface.main};
    --ag-checkbox-checked-shape-color: ${({ theme }) =>
      theme.colours.graphics.inverse.main};
    --ag-checkbox-indeterminate-background-color: ${({ theme }) =>
      theme.colours.interface.main};
    --ag-checkbox-indeterminate-border-color: ${({ theme }) =>
      theme.colours.interface.main};
    --ag-checkbox-indeterminate-shape-color: ${({ theme }) =>
      theme.colours.graphics.inverse.main};
  }

  /* Quarter dividers live in AG Grid's full-width container, separate from
     the data rows. The shared ".ag-row:not(:last-child)" divider rule counts
     "last" per container, so it skips the final divider (4th Quarter).
     Draw the line on every divider explicitly. */
  .ag-full-width-row {
    border-bottom: 1px solid
      ${({ theme }) => theme.colours.canvas.secondary.strong};
  }

  /* The shared ".ag-row:not(:last-child)" rule skips the last row so a full
     table doesn't double up against the outer border. These grids always
     fill the card, so a short table leaves space under the last row and the
     missing line shows. Draw the line on every row. */
  .ag-row {
    border-bottom: 1px solid
      ${({ theme }) => theme.colours.canvas.secondary.strong};
  }

  /* Header filter and menu icons use the body text colour instead of the
     header text colour. The SVG icons draw in their parent's text colour. */
  .ag-header-icon {
    color: ${({ theme }) => theme.colours.ink.primary.main};
  }

  /* Keep AG Grid's own icon hover colour. The rule above would otherwise
     win over it, so the icons would no longer change on hover. */
  .ag-header-icon:hover {
    color: var(--ag-icon-button-hover-color);
  }

  /* Line under the header row uses the same colour as the row dividers
     instead of the shared styles' interface.main (blue). Width stays as the
     shared styles set it. */
  .ag-header {
    border-bottom-color: ${({ theme }) =>
      theme.colours.canvas.secondary.strong};
  }

  /* Divider between sticky (pinned-left) and scrolling columns: one line on
     the pinned area's outer edge, in the same width as the line under the
     header row. */
  .ag-pinned-left-header,
  .ag-pinned-left-cols-container {
    border-right-width: ${({ theme }) => theme.sizing.xtiny} !important;
  }

  /* Drop the vertical divider the shared styles draw to the left of the
     pinned-right action column (drive table and sub-table). */
  .ag-pinned-right-header,
  .ag-pinned-right-cols-container {
    border-left: none !important;
  }

  /* The drive sub-table has its own outer border directly below its last
     row, so keep that row's line off to avoid a doubled line. */
  .ag-details-row .ag-row:last-child {
    border-bottom-color: transparent;
  }
`;

/* Quarter divider: full-width row background + body-style label. */
export const QuarterDivider = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 ${({ theme }) => theme.sizing.medium};
  background: ${({ theme }) => theme.colours.canvas.secondary.main};
  ${({ theme }) => theme.typography.bodyMedium};
  color: ${({ theme }) => theme.colours.ink.primary.strong};
`;

export const VideoModalWrap = styled.div`
  > dialog {
    width: calc(100% - ${({ theme }) => theme.sizing.xxlarge});
    max-width: 1280px;
    max-height: calc(100vh - ${({ theme }) => theme.sizing.xxlarge});
    margin: auto;
    padding: 0;
    background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
    overflow: hidden;
  }
`;

export const VideoModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 3.5rem;

  .close {
    position: absolute;
    right: ${({ theme }) => theme.sizing.medium};
    top: ${({ theme }) => theme.sizing.medium};
  }
`;

// DriveTableWrap: kept for DrivesTable.jsx backward compatibility.
export const DriveTableWrap = styled.div`
  max-width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    ${({ theme }) => theme.typography.bodyMedium};
    color: ${({ theme }) => theme.colours.ink.primary.main};
  }

  th,
  td {
    padding: ${({ theme }) => theme.sizing.small};
    border-bottom: 1px solid
      ${({ theme }) => theme.colours.canvas.secondary.strong};
    text-align: left;
    white-space: nowrap;
  }

  th {
    color: ${({ theme }) => theme.colours.ink.primary.weak};
    font-weight: 600;
  }

  .quarter-divider th {
    background: ${({ theme }) => theme.colours.canvas.secondary.weak};
    color: ${({ theme }) => theme.colours.ink.primary.main};
    ${({ theme }) => theme.typography.headingSmall};
  }

  .team-dot {
    display: inline-block;
    vertical-align: middle;
    width: 1rem;
    height: 1rem;
    margin-right: ${({ theme }) => theme.sizing.tiny};
    border: 1px solid;
    border-radius: 50%;
  }

  .yards-bar {
    display: block;
    min-width: 2px;
    height: 0.5rem;
    background: ${({ theme }) => theme.colours.interface.main};
  }

  td:nth-child(3),
  td:nth-child(8),
  td:nth-child(9) {
    white-space: normal;
  }

  .drive-detail > td {
    padding: 0 0 ${({ theme }) => theme.sizing.medium}
      ${({ theme }) => theme.sizing.large};
    background: ${({ theme }) => theme.colours.canvas.secondary.weak};
  }

  button {
    cursor: pointer;
  }
`;
DriveTableWrap.displayName = 'DriveTableWrap';

/* EmptyState: centred icon + heading + body stack, used inside a Card when
   there are no games for the selected team. Mirrors the Scout "No Results"
   pattern so the visual treatment is consistent across the app. The parent
   PlayByPlayCardWrap provides the full-viewport height; this fills it. */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: ${({ theme }) => theme.sizing.tiny};
  padding: ${({ theme }) => theme.sizing.large};

  svg {
    color: ${({ theme }) => theme.colours.messaging.info.main};
  }
`;
EmptyState.displayName = 'EmptyState';
